import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SatisfinderForgotPassword() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [step, setStep] = useState('request'); // request | reset | done
  const [email, setEmail] = useState(sp.get('email') || '');
  const [code, setCode] = useState('');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [busy, setBusy] = useState(false);

  const sendCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    await fetch(`${API}/satisfinder/forgot-password`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    setBusy(false);
    setStep('reset');
    toast.success('If that email is registered, a reset code has been sent.');
  };

  const reset = async (e) => {
    e.preventDefault();
    if (pw !== pw2) { toast.error('Passwords do not match'); return; }
    setBusy(true);
    const res = await fetch(`${API}/satisfinder/reset-password`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, code, new_password: pw, confirm_password: pw2 }),
    });
    setBusy(false);
    if (!res.ok) { const d = await res.json().catch(() => ({})); toast.error(d.detail || 'Reset failed'); return; }
    setStep('done');
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-6">
      <div className="w-full max-w-md bg-white rounded-2xl border border-[#E2E8F0] p-8 shadow-sm" data-testid="sf-forgot-form">
        <Link to="/satisfinder" className="flex items-center gap-2">
          <img src="/satisfind-logo.png" alt="satisFIND" style={{ height: 36, width: 'auto' }} />
        </Link>

        {step === 'request' && (
          <>
            <h1 className="font-['Montserrat'] text-2xl font-bold mt-6">Reset your password</h1>
            <p className="text-sm text-[#475569] mt-2">Enter the email address linked to your satisFINDER® account. We&apos;ll send you a verification code to reset your password.</p>
            <form onSubmit={sendCode} className="mt-6 space-y-4">
              <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com"
                className="w-full px-3 py-2.5 border border-[#CBD5E1] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1A3E6F]"
                data-testid="sf-forgot-email" />
              <button type="submit" disabled={busy || !email} className="w-full bg-[#1A3E6F] hover:bg-[#12305A] disabled:bg-[#94A3B8] text-white font-semibold py-3 rounded-lg text-sm" data-testid="sf-forgot-submit">
                {busy ? 'Sending…' : 'Send reset code'}
              </button>
            </form>
          </>
        )}

        {step === 'reset' && (
          <>
            <h1 className="font-['Montserrat'] text-2xl font-bold mt-6">Check your email</h1>
            <p className="text-sm text-[#475569] mt-2">Enter the verification code we sent to your email address.</p>
            <form onSubmit={reset} className="mt-6 space-y-4">
              <input required value={code} onChange={(e) => setCode(e.target.value)} placeholder="Verification code"
                className="w-full px-3 py-2.5 border border-[#CBD5E1] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1A3E6F]"
                data-testid="sf-reset-code" />
              <input required type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password"
                className="w-full px-3 py-2.5 border border-[#CBD5E1] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1A3E6F]"
                data-testid="sf-reset-pw1" />
              <input required type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} placeholder="Confirm new password"
                className="w-full px-3 py-2.5 border border-[#CBD5E1] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1A3E6F]"
                data-testid="sf-reset-pw2" />
              <button type="submit" disabled={busy} className="w-full bg-[#1A3E6F] hover:bg-[#12305A] disabled:bg-[#94A3B8] text-white font-semibold py-3 rounded-lg text-sm" data-testid="sf-reset-submit">
                {busy ? 'Resetting…' : 'Reset password'}
              </button>
            </form>
          </>
        )}

        {step === 'done' && (
          <div className="mt-6" data-testid="sf-reset-done">
            <h1 className="font-['Montserrat'] text-2xl font-bold">Password reset</h1>
            <p className="text-sm text-[#475569] mt-2">Your password has been reset. You can now log in with your new password.</p>
            <button onClick={() => nav('/satisfinder/login')} className="mt-6 w-full bg-[#1A3E6F] hover:bg-[#12305A] text-white font-semibold py-3 rounded-lg text-sm">
              Back to login
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
